<template>
    <Preloader v-if="!isLoaded" :message="'Cargando enfermedades...'" />
    <transition name="fade">
        <div v-if="isLoaded" class="diseases-container-modern">

            <div v-if="isCreatingDisease" class="create-card">
                <div class="create-header">
                    <div>
                        <p class="category-index">Nuevo registro</p>
                        <h5>Crear enfermedad heredofamiliar</h5>
                    </div>
                    <div class="header-actions">
                        <button class="ghost-button" @click="closeCreateForm">
                            <i class="fas fa-times"></i>
                            Cancelar
                        </button>
                    </div>
                </div>
                <CreateDisease
:new-disease-name="newDiseaseName || searchQuery" :expanded-cards="expandedCards"
                    :save-to-local-storage="saveToLocalStorage" :patient-record="patientRecord"
                    @update-disease="updateDisease" @create-disease="handleCreateNewDisease" />
            </div>

            <template v-else>
                <div class="section-header-modern">
                    <div class="header-title-modern">
                        <i class="fas fa-users-medical"></i>
                        <h4>Enfermedades Heredofamiliares</h4>
                    </div>
                </div>

                <div class="controls-row">
                    <SearchInput
v-model="searchQuery" placeholder="Buscar enfermedad..."
                        @clear-search="handleClearSearch" />

                    <div class="actions-group">
                        <div class="pill-filters">
                            <button
v-for="option in filterOptions" :key="option.value"
                                :class="['pill', { active: currentTab === option.value }]"
                                @click="setFilterMode(option.value)">
                                <span class="dot" :class="option.dotClass"></span>
                                {{ option.label }}
                                <span class="pill-count">{{ option.count }}</span>
                            </button>
                        </div>
                        <button class="action-btn" @click="openCreateForm(searchQuery)">
                            <i class="fas fa-plus"></i>
                            Crear nueva
                        </button>
                    </div>
                </div>

                <transition-group name="fade" tag="div" class="diseases-list">
                    <div v-for="category in filteredCategories" :key="category.id" class="category-section-clean">
                        <div class="category-header-clean">
                            <h5>{{ category.name }}</h5>
                            <span class="count-badge">{{ category.visibleDiseases.length }}</span>
                        </div>
                        <transition-group name="fade" tag="ul" class="disease-grid">
                            <li
v-for="disease in category.visibleDiseases" :key="disease.id"
                                :class="['disease-item-card', { inactive: !disease.active, expanded: disease.isOpen }]">
                                <div class="disease-header" @click="toggleCard(category.id, disease.id)">
                                    <div class="disease-info">
                                        <h4>{{ disease.name }}</h4>
                                        <div
v-if="!disease.isOpen && (disease.family_members?.length || disease.description)"
                                            class="summary-row">
                                            <span v-if="disease.family_members?.length" class="summary-text">
                                                {{ disease.family_members.join(', ') }}
                                            </span>
                                            <span
v-if="disease.family_members?.length && disease.description"
                                                class="separator">•</span>
                                            <span v-if="disease.description" class="summary-description">
                                                {{ disease.description }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="header-controls">
                                        <span v-if="!disease.active" class="status-badge archived">Archivada</span>
                                        <div class="icon-box">
                                            <i
                                                :class="disease.isOpen ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                                        </div>
                                    </div>
                                </div>

                                <transition name="fade">
                                    <div v-if="disease.isOpen" class="disease-content">
                                        <template v-if="disease.active">
                                            <div class="content-wrapper">
                                                <div class="section-label">Parentesco</div>
                                                <div class="checkbox-grid">
                                                    <div
v-for="relation in relations" :key="relation"
                                                        class="checkbox-item-modern">
                                                        <argon-checkbox
                                                            :id="relation + '-' + category.id + '-' + disease.id"
                                                            :checked="disease.family_members.includes(relation)"
                                                            @change="handleCheckboxChange(category.id, disease.id, relation)">
                                                        </argon-checkbox>
                                                        <label :for="relation + '-' + category.id + '-' + disease.id">{{
                                                            relation }}</label>
                                                    </div>
                                                </div>

                                                <div class="notes-section">
                                                    <div class="section-label">Notas adicionales</div>
                                                    <input
:id="'description-' + category.id + '-' + disease.id"
                                                        v-model="disease.description" type="text" class="modern-input"
                                                        placeholder="Escribe detalles específicos aquí..."
                                                        @input="handleDescriptionChange(category.id, disease.id, $event.target.value)" />
                                                </div>

                                                <div class="card-actions">
                                                    <button
class="text-danger-btn"
                                                        @click.stop="deleteDisease(category.id, disease.id)">
                                                        <i class="fa-regular fa-trash-can"></i> Archivar enfermedad
                                                    </button>
                                                </div>
                                            </div>
                                        </template>
                                        <template v-else>
                                            <div class="archived-state">
                                                <p>Esta enfermedad está archivada y no aparece en los reportes
                                                    principales.</p>
                                                <button
class="restore-btn"
                                                    @click.stop="restoreDisease(category.id, disease.id)">
                                                    <i class="fa-solid fa-rotate-left"></i> Restaurar
                                                </button>
                                            </div>
                                        </template>
                                    </div>
                                </transition>
                            </li>
                        </transition-group>
                    </div>
                    <div v-if="showCreateButton && !isCreatingDisease" class="create-disease-hint">
                        <div>
                            <p class="muted">No encontramos "{{ searchQuery }}".</p>
                            <p class="preview-text">¿Quieres registrarla como nueva enfermedad?</p>
                        </div>
                        <button class="soft-pill" @click="openCreateForm(searchQuery)">
                            <i class="fas fa-wand-magic-sparkles"></i>
                            Crear "{{ searchQuery }}"
                        </button>
                    </div>
                </transition-group>

                <div v-if="!filteredCategories.length && !showCreateButton" class="empty-state">
                    <i class="fas fa-search"></i>
                    <p>No encontramos coincidencias. Prueba otro término o crea una nueva enfermedad.</p>
                </div>
            </template>

            <button class="reset-button hidden" @click="resetLocalStorageHandler">Resetear LocalStorage</button>
        </div>
    </transition>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import ArgonCheckbox from "@/components/ArgonCheckbox.vue";
import '@/assets/css/diseasesData.css';
import SearchInput from '@/components/SearchInput.vue';
// ✅ Import relativo - mismo directorio
import CreateDisease from './CreateDisease.vue';
import Preloader from '@/components/Preloader.vue';

import { loadDiseasesFromAPI, updateDisease } from '@/store/Expedientes/ApiDiseases';
import { loadPatientDiseasesFromLocalStorage, saveToLocalStorage, resetLocalStorage } from '@/store/Expedientes/localStorageDiseases';

const props = defineProps({
    patientRecord: {
        type: Object,
        required: true
    }
});

const relations = ['Abuelos', 'Padre', 'Madre', 'Hermanos', 'Otros'];
const categories = ref([]);
const expandedCards = ref([]);
const allDiseasesOpen = ref(false);
const searchQuery = ref('');
const isCreatingDisease = ref(false);
const selectedDisease = ref(null);
const isLoaded = ref(false);
const filterMode = ref('active');
const showOnlyWithData = ref(true);
const currentTab = ref('withData');

const initializeExpandedCards = () => {
    const patientDiseases = loadPatientDiseasesFromLocalStorage(props.patientRecord.id);
    expandedCards.value = categories.value.map(category => ({
        ...category,
        diseases: category.diseases.map(disease => {
            const patientDisease = patientDiseases.find(pd => pd.id === disease.id) || {};
            return {
                ...disease,
                isOpen: false,
                description: patientDisease.description || disease.description || '',
                family_members: patientDisease.family_members || disease.family_members || [],
                active: patientDisease.active !== undefined ? patientDisease.active : disease.active !== undefined ? disease.active : true
            };
        })
    }));
};

const handleCheckboxChange = (categoryId, diseaseId, relation) => {
    const category = expandedCards.value.find(cat => cat.id === categoryId);
    if (category) {
        const disease = category.diseases.find(dis => dis.id === diseaseId);
        if (disease) {
            const index = disease.family_members.indexOf(relation);
            if (index === -1) {
                disease.family_members.push(relation);
            } else {
                disease.family_members.splice(index, 1);
            }
            updateDisease(props.patientRecord.id, disease);
        }
    }
};

const handleDescriptionChange = (categoryId, diseaseId, newDescription) => {
    const category = expandedCards.value.find(cat => cat.id === categoryId);
    if (category) {
        const disease = category.diseases.find(dis => dis.id === diseaseId);
        if (disease) {
            disease.description = newDescription;
            updateDisease(props.patientRecord.id, disease);
        }
    }
};

const toggleCard = (categoryId, diseaseId) => {
    const category = expandedCards.value.find(cat => cat.id === categoryId);
    if (category) {
        const disease = category.diseases.find(dis => dis.id === diseaseId);
        if (disease) {
            disease.isOpen = !disease.isOpen;
            saveToLocalStorage(props.patientRecord.id, expandedCards.value);
        }
    }
};

const handleClearSearch = async () => {
    searchQuery.value = '';
    isCreatingDisease.value = false;
    selectedDisease.value = null;
    categories.value = await loadDiseasesFromAPI(props.patientRecord.id);
    initializeExpandedCards();
    setFilterMode('withData');
    allDiseasesOpen.value = false;
};

const hasData = (disease) => {
    return Boolean(
        (disease.description && disease.description.trim().length > 0) ||
        (Array.isArray(disease.family_members) && disease.family_members.length > 0)
    );
};

const countWithData = computed(() => {
    return expandedCards.value.reduce((sum, cat) => {
        return sum + cat.diseases.filter(d => d.active && hasData(d)).length;
    }, 0);
});

const countAllActive = computed(() => {
    return expandedCards.value.reduce((sum, cat) => {
        return sum + cat.diseases.filter(d => d.active).length;
    }, 0);
});

const countArchived = computed(() => {
    return expandedCards.value.reduce((sum, cat) => {
        return sum + cat.diseases.filter(d => !d.active).length;
    }, 0);
});

const filteredCategories = computed(() => {
    return expandedCards.value
        .map(category => ({
            ...category,
            visibleDiseases: category.diseases.filter(disease => {
                const normalizedQuery = normalizeString(searchQuery.value);
                const matchesSearch = normalizedQuery ? normalizeString(disease.name).includes(normalizedQuery) : true;
                const matchesFilter = filterMode.value === 'active'
                    ? disease.active
                    : filterMode.value === 'archived'
                        ? !disease.active
                        : true;
                const matchesData = showOnlyWithData.value ? hasData(disease) : true;
                return matchesSearch && matchesFilter && matchesData;
            })
        }))
        .filter(category => category.visibleDiseases.length > 0);
});

const normalizeString = (str) => {
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
};

const showCreateButton = computed(() => {
    return searchQuery.value && filteredCategories.value.every(category => category.visibleDiseases.length === 0);
});

const newDiseaseName = ref('');

const deleteDisease = (categoryId, diseaseId) => {
    const category = expandedCards.value.find(cat => cat.id === categoryId);
    if (category) {
        const disease = category.diseases.find(dis => dis.id === diseaseId);
        if (disease) {
            disease.active = false;
            saveToLocalStorage(props.patientRecord.id, expandedCards.value);
            updateDisease(props.patientRecord.id, disease);
        }
    }
};

const restoreDisease = (categoryId, diseaseId) => {
    const category = expandedCards.value.find(cat => cat.id === categoryId);
    if (category) {
        const disease = category.diseases.find(dis => dis.id === diseaseId);
        if (disease) {
            disease.active = true;
            saveToLocalStorage(props.patientRecord.id, expandedCards.value);
            updateDisease(props.patientRecord.id, disease);
        }
    }
};

const resetLocalStorageHandler = () => {
    resetLocalStorage(props.patientRecord.id);
};

const filterOptions = computed(() => ([
    { value: 'withData', label: 'Activas', count: countWithData.value, dotClass: 'active' },
    { value: 'all', label: 'Catálogo de enfermedades', count: countAllActive.value, dotClass: 'all' },
    { value: 'archived', label: 'Archivadas', count: countArchived.value, dotClass: 'archived' }
]));

const setFilterMode = (mode) => {
    currentTab.value = mode;
    if (mode === 'withData') {
        filterMode.value = 'active';
        showOnlyWithData.value = true;
    } else if (mode === 'all') {
        filterMode.value = 'active';
        showOnlyWithData.value = false;
    } else if (mode === 'archived') {
        filterMode.value = 'archived';
        showOnlyWithData.value = false;
    }
};

const openCreateForm = (name = '') => {
    newDiseaseName.value = name || '';
    isCreatingDisease.value = true;
    showOnlyWithData.value = false;
    allDiseasesOpen.value = false;
};

const closeCreateForm = () => {
    isCreatingDisease.value = false;
    newDiseaseName.value = '';
    showOnlyWithData.value = true;
    allDiseasesOpen.value = false;
};

const handleCreateNewDisease = async () => {
    await handleClearSearch();
    isCreatingDisease.value = false;
    showOnlyWithData.value = true;
    allDiseasesOpen.value = false;
};

watch(() => props.patientRecord.id, () => {
    showOnlyWithData.value = true;
    allDiseasesOpen.value = false;
});

onMounted(async () => {
    categories.value = await loadDiseasesFromAPI(props.patientRecord.id);
    initializeExpandedCards();
    isLoaded.value = true;
});

watch(expandedCards, () => saveToLocalStorage(props.patientRecord.id, expandedCards.value), { deep: true });

watch(searchQuery, (newQuery) => {
    if (showCreateButton.value) {
        newDiseaseName.value = newQuery;
        isCreatingDisease.value = true;
    } else {
        newDiseaseName.value = '';
        isCreatingDisease.value = false;
    }
});
</script>

<style scoped>
.diseases-container-modern {
    background: #ffffff;
    border-radius: 12px;
    padding: 1rem;
    margin-bottom: 1rem;
    border: 1px solid #f1f5f9;
}

.section-header-modern {
    margin-bottom: 0.5rem;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid #f0f2f5;
}

.header-title-modern {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.header-title-modern i {
    font-size: 1.5rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
}

.header-title-modern h4 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    color: #344767;
}

.header-content {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.header-top {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    align-items: center;
}

.header-top h4 {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 800;
    color: #0f172a;
}

.header-subtitle {
    margin: 0.1rem 0 0;
    color: #64748b;
    font-size: 0.95rem;
}

.header-actions {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
    justify-content: flex-end;
}

.header-bottom {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.ghost-button {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.4rem 0.7rem;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    border-radius: 10px;
    color: #0f172a;
    font-weight: 600;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s ease;
}

.ghost-button:hover {
    border-color: #cbd5e1;
    transform: translateY(-1px);
}

.controls-row {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
}

.actions-group {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
}

.pill-filters {
    display: flex;
    gap: 0.5rem;
    flex-wrap: wrap;
}

.pill {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.4rem 0.75rem;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #ffffff;
    color: #64748b;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 0.85rem;
}

.pill:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
}

.pill.active {
    background: #f1f5f9;
    color: #0f172a;
    border-color: #cbd5e1;
}

.dot {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: #cbd5e1;
}

.dot.active {
    background: #10b981;
}

.dot.archived {
    background: #ef4444;
}

.dot.all {
    background: #3b82f6;
}

.pill-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 999px;
    background: #f1f5f9;
    color: #64748b;
    font-size: 0.75rem;
    margin-left: 4px;
}

.pill.active .pill-count {
    background: #ffffff;
    color: #0f172a;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.action-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.4rem 0.75rem;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    color: #0f172a;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 0.85rem;
}

.action-btn:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    transform: translateY(-1px);
}

.soft-pill {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.45rem 0.75rem;
    border-radius: 999px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    color: #0f172a;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);
}

.soft-pill.active {
    border-color: #5e72e4;
    color: #5e72e4;
    box-shadow: 0 8px 20px rgba(94, 114, 228, 0.18);
    background: #f5f7ff;
}

.soft-pill:hover {
    transform: translateY(-1px);
    border-color: #cbd5e1;
}

.category-section-clean {
    margin-bottom: 1.5rem;
}

.category-header-clean {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
    padding-left: 0.25rem;
}

.category-header-clean h5 {
    margin: 0;
    font-size: 0.95rem;
    color: #64748b;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.count-badge {
    background: #f1f5f9;
    color: #64748b;
    font-size: 0.7rem;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
    font-weight: 600;
}

.summary-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.25rem;
    flex-wrap: wrap;
}

.summary-text {
    font-size: 0.8rem;
    color: #64748b;
    font-weight: 500;
}

.summary-description {
    font-size: 0.8rem;
    color: #94a3b8;
    font-style: italic;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 300px;
}

.separator {
    color: #cbd5e1;
    font-size: 0.6rem;
}

.disease-grid {
    list-style: none;
    padding: 0;
    margin: 0;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 0.75rem;
}

.disease-item-card {
    border: 1px solid #f1f5f9;
    border-radius: 12px;
    background: #fff;
    transition: all 0.2s ease;
    overflow: hidden;
}

.disease-item-card.expanded {
    border-color: #e2e8f0;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
}

.disease-item-card.inactive {
    opacity: 0.75;
    background: #f8fafc;
}

.disease-item-card:hover {
    border-color: #cbd5e1;
}

.disease-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.25rem;
    cursor: pointer;
    background: #fff;
}

.disease-info h4 {
    margin: 0;
    font-size: 0.95rem;
    color: #0f172a;
    font-weight: 600;
}

.summary-text {
    font-size: 0.8rem;
    color: #64748b;
    margin-top: 0.2rem;
    display: block;
}

.header-controls {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.status-badge {
    font-size: 0.7rem;
    padding: 0.2rem 0.6rem;
    border-radius: 999px;
    font-weight: 600;
}

.status-badge.archived {
    background: #fee2e2;
    color: #991b1b;
}

.icon-box {
    width: 24px;
    height: 24px;
    display: grid;
    place-items: center;
    color: #94a3b8;
    transition: color 0.2s;
}

.disease-header:hover .icon-box {
    color: #3b82f6;
}

.disease-content {
    border-top: 1px solid #f1f5f9;
    background: #fcfcfc;
    padding: 1.25rem;
}

.section-label {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #94a3b8;
    font-weight: 700;
    margin-bottom: 0.75rem;
}

.checkbox-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 0.75rem;
    margin-bottom: 1.5rem;
}

.checkbox-item-modern {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.checkbox-item-modern label {
    font-size: 0.9rem;
    color: #334155;
    cursor: pointer;
    margin: 0;
}

.notes-section {
    margin-bottom: 1.5rem;
}

.modern-input {
    width: 100%;
    padding: 0.6rem 0.8rem;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.9rem;
    color: #0f172a;
    background: #fff;
    transition: all 0.2s;
}

.modern-input:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.card-actions {
    display: flex;
    justify-content: flex-end;
    border-top: 1px dashed #e2e8f0;
    padding-top: 1rem;
}

.text-danger-btn {
    background: none;
    border: none;
    color: #ef4444;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 0.8rem;
    border-radius: 6px;
    transition: background 0.2s;
}

.text-danger-btn:hover {
    background: #fef2f2;
}

.archived-state {
    text-align: center;
    padding: 1rem;
}

.archived-state p {
    color: #64748b;
    font-size: 0.9rem;
    margin-bottom: 1rem;
}

.restore-btn {
    background: #fff;
    border: 1px solid #cbd5e1;
    color: #0f172a;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-weight: 600;
    font-size: 0.9rem;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.2s;
}

.restore-btn:hover {
    border-color: #3b82f6;
    color: #3b82f6;
}


.empty-state {
    padding: 1.5rem;
    text-align: center;
    color: #64748b;
    border: 1px dashed #e2e8f0;
    border-radius: 12px;
    background: #f8fafc;
    margin-top: 0.5rem;
}

.create-disease-hint {
    border: 1px dashed #cbd5e1;
    border-radius: 12px;
    padding: 1rem;
    background: #f8fafc;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
}

.empty-state i {
    display: block;
    font-size: 1.4rem;
    margin-bottom: 0.4rem;
    color: #94a3b8;
}

.hidden {
    display: none;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

/* Compact Controls & Stats */
.controls-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.5rem;
    flex-wrap: wrap;
}

.stats-row {
    display: flex;
    justify-content: space-around;
    padding: 0.5rem;
    background: #f8f9fa;
    border-radius: 8px;
    margin-bottom: 0.5rem;
}

.stat-card {
    text-align: center;
}

.stat-card h3 {
    font-size: 1rem;
    margin: 0;
    color: #344767;
}

.stat-card p {
    font-size: 0.75rem;
    margin: 0;
    color: #8392ab;
}

.pill-filters {
    display: flex;
    gap: 0.5rem;
}

.pill {
    padding: 0.25rem 0.75rem;
    border-radius: 20px;
    border: 1px solid #e9ecef;
    background: white;
    font-size: 0.75rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.2s;
}

.pill.active {
    background: #344767;
    color: white;
    border-color: #344767;
}

.pill-count {
    background: rgba(0, 0, 0, 0.1);
    padding: 0 0.4rem;
    border-radius: 10px;
    font-size: 0.7rem;
}

.pill.active .pill-count {
    background: rgba(255, 255, 255, 0.2);
}

@media (max-width: 768px) {
    .controls-row {
        flex-direction: column;
        align-items: flex-start;
    }

    .description-container {
        flex-direction: column;
        align-items: flex-start;
    }

    .archived-row {
        flex-direction: column;
        align-items: flex-start;
    }
}
</style>
